import { useEffect, useRef, useState } from "react";

/**
 * useBpmAnalyzer — fetches + decodes + analyzes a track's audio for BPM.
 *
 * Uses `web-audio-beat-detector` which implements the Joe Turner autocorrelation
 * algorithm against a decoded AudioBuffer. Fully offline — one-shot analysis
 * on track change. Results cached per track source_url so re-loading the same
 * track doesn't re-download.
 *
 * Returns { bpm, analyzing, error }.
 *   • bpm      number | null — the detected BPM (rounded to 0.1)
 *   • analyzing true while fetch + decode is in flight
 *   • error    string | null
 *
 * Failure modes (fall back to null cleanly):
 *   • CORS-blocked source — web-audio-beat-detector needs the raw PCM.
 *     For iTunes preview URLs this works because they serve CORS *.
 *   • YouTube / Spotify / search URLs (not directly playable) — we skip.
 *   • Very short clips (<4s) — analyzer may return unstable result; we guard.
 */
const bpmCache = new Map();

export default function useBpmAnalyzer(sourceUrl, enabled = true) {
  const [bpm, setBpm] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    setBpm(null); setError(null);
    if (!enabled || !sourceUrl) return;
    // Skip non-playable URLs
    const u = String(sourceUrl).toLowerCase();
    if (u.includes("youtube.com/results") || u.includes("spotify.com/")) return;
    if (bpmCache.has(sourceUrl)) {
      setBpm(bpmCache.get(sourceUrl));
      return;
    }
    (async () => {
      setAnalyzing(true);
      try {
        const res = await fetch(sourceUrl, { mode: "cors" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const arr = await res.arrayBuffer();
        if (cancelRef.current) return;
        const AC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        // Probe sample rate via a temporary AudioContext
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const probe = new Ctx();
        const buf = await probe.decodeAudioData(arr.slice(0));
        try { probe.close(); } catch {}
        if (cancelRef.current) return;
        if (buf.duration < 4) throw new Error("too short");
        const { analyze } = await import("web-audio-beat-detector");
        const detected = await analyze(buf);
        if (cancelRef.current) return;
        const rounded = Math.round(detected * 10) / 10;
        bpmCache.set(sourceUrl, rounded);
        setBpm(rounded);
      } catch (e) {
        if (!cancelRef.current) setError(String(e?.message || e));
      } finally {
        if (!cancelRef.current) setAnalyzing(false);
      }
    })();
    return () => { cancelRef.current = true; };
  }, [sourceUrl, enabled]);

  return { bpm, analyzing, error };
}
